import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, Divider, IconButton, MenuItem, Stack, Switch, TextField,
  Tooltip, Typography, Alert, CircularProgress, Paper,
} from "@mui/material";
import { LocalizationProvider, TimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventIcon from "@mui/icons-material/Event";
import {
  useGetPlaylistGroupsQuery,
  useGetPlaylistsQuery,
  useAddPlaylistToGroupMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import {
  IPlaylist, IPlaylistGroup, IScheduleConflict,
} from "../../../Api/Interface/api.interface";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import { useNotifier } from "../../../Core/Notifier";
import { NAVY, NAVY_DARK, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY } from "../../../theme/tokens";
import SignageSection, { signageLabelSx, signageInputSx } from "../../../Components/ui/SignageSection";
import {
  WEEK_DAYS, dayIsSet, toggleDay, to24h, to12h,
} from "../../Playlist/Edit/menuScheduleShared";

/** Build the plain-language "…will play … on N screens" summary. */
const buildSummary = (
  playlistName: string, groupName: string, days: number, allDay: boolean,
  start: dayjs.Dayjs | null, end: dayjs.Dayjs | null,
  startDate: dayjs.Dayjs | null, endDate: dayjs.Dayjs | null,
  screens: number,
): string => {
  const name = playlistName || "This menu";
  const daysPhrase = !days
    ? "every day"
    : "every " + WEEK_DAYS.filter((_, i) => (days & (1 << i)) !== 0).join(", ");
  const timePhrase = allDay || (!start && !end)
    ? "all day"
    : `from ${to12h(to24h(start))} to ${to12h(to24h(end))}`;
  let datePhrase = "";
  if (startDate && endDate) datePhrase = ` between ${startDate.format("MMM D")} and ${endDate.format("MMM D")}`;
  else if (startDate) datePhrase = ` from ${startDate.format("MMM D")}`;
  else if (endDate) datePhrase = ` until ${endDate.format("MMM D")}`;
  const screensPhrase = ` on ${screens} screen${screens === 1 ? "" : "s"}`;
  return `${name} will play in ${groupName} ${daysPhrase} ${timePhrase}${datePhrase}${screensPhrase}.`;
};

const MenuScheduleCreate: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const [companyId, setCompanyId] = useState<number | "">("");
  const [groupId, setGroupId] = useState<number | "">("");
  const [playlistId, setPlaylistId] = useState<number | "">("");
  const [days, setDays] = useState(0);
  const [allDay, setAllDay] = useState(true);
  const [start, setStart] = useState<dayjs.Dayjs | null>(null);
  const [end, setEnd] = useState<dayjs.Dayjs | null>(null);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [priority, setPriority] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [conflicts, setConflicts] = useState<IScheduleConflict[] | null>(null);

  const [addPlaylist, { isLoading: saving }] = useAddPlaylistToGroupMutation();

  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray((companiesData as any)?.data)
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>) : [];

  const { data: groupsData } = useGetPlaylistGroupsQuery(
    companyId ? { company_id: Number(companyId) } : undefined
  );
  const groups: IPlaylistGroup[] = Array.isArray(groupsData?.data)
    ? ((groupsData?.data as unknown) as IPlaylistGroup[]) : [];

  const { data: playlistsData } = useGetPlaylistsQuery({ searchText: "" });
  const allPlaylists: IPlaylist[] = Array.isArray(playlistsData?.data)
    ? ((playlistsData?.data as unknown) as IPlaylist[]) : [];

  const selectedGroup = groups.find((g) => g.id === groupId) ?? null;
  const effectiveCompanyId = selectedGroup?.company_id ?? (companyId === "" ? null : Number(companyId));

  // Only offer playlists that belong to the schedule's company and are active.
  const playlists = useMemo(
    () => allPlaylists.filter((p) =>
      p.is_active && (effectiveCompanyId == null || p.company_id === effectiveCompanyId)),
    [allPlaylists, effectiveCompanyId]
  );
  const selectedPlaylist = playlists.find((p) => p.id === playlistId) ?? null;

  const screens = selectedGroup?.devices_count ?? 0;
  const canSave = groupId !== "" && playlistId !== "" && !saving;

  const summary = groupId !== "" && playlistId !== ""
    ? buildSummary(selectedPlaylist?.name ?? "", selectedGroup?.name ?? "",
        days, allDay, start, end, startDate, endDate, screens)
    : "";

  const save = async (force = false) => {
    if (groupId === "" || playlistId === "") return;
    const body = {
      id: Number(groupId),
      playlist_id: Number(playlistId),
      is_active: isActive,
      priority,
      days_of_week: days || null,
      start_time: allDay ? null : to24h(start),
      end_time: allDay ? null : to24h(end),
      start_date: startDate ? startDate.format("YYYY-MM-DD") : null,
      end_date: endDate ? endDate.format("YYYY-MM-DD") : null,
      ...(force ? { force: true } : {}),
    };
    try {
      await addPlaylist(body).unwrap();
      showMessage("Menu schedule created");
      navigate(`/menu-schedules/${Number(groupId)}`);
    } catch (e: any) {
      const c = e?.data?.data?.conflicts as IScheduleConflict[] | undefined
        ?? (e?.data?.conflicts as IScheduleConflict[] | undefined);
      if (c && c.length) { setConflicts(c); }
      else { showErrorMessage(e?.data?.message || "Could not create the schedule."); }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
        {/* Header */}
        <Box sx={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
          px: 3, py: 2, display: "flex", alignItems: "center", gap: 2,
        }}>
          <IconButton onClick={() => navigate("/menu-schedules/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
          }}>
            <EventIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography fontSize={20} fontWeight={800} color="#fff">Create Menu Schedule</Typography>
            <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
              Schedule when &amp; how a menu becomes active on its screens
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, maxWidth: 760 }}>
          <SignageSection icon={<EventIcon sx={{ fontSize: 18, color: "#0891b2" }} />}
            title="What to schedule" accentColor="#0891b2">
            {isSuperAdmin && (
              <>
                <Typography sx={signageLabelSx}>Company *</Typography>
                <TextField select fullWidth size="small" value={companyId === "" ? "" : String(companyId)}
                  onChange={(e) => { setCompanyId(e.target.value === "" ? "" : Number(e.target.value)); setGroupId(""); setPlaylistId(""); }}
                  sx={{ mb: 2, ...signageInputSx }}>
                  <MenuItem value="">— Select a company —</MenuItem>
                  {companies.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.companyName}</MenuItem>)}
                </TextField>
              </>
            )}

            <Typography sx={signageLabelSx}>Playlist Group (screens) *</Typography>
            <TextField select fullWidth size="small" value={groupId === "" ? "" : String(groupId)}
              onChange={(e) => { setGroupId(e.target.value === "" ? "" : Number(e.target.value)); setPlaylistId(""); }}
              helperText="The group decides which screens this schedule plays on."
              sx={{ mb: 2, ...signageInputSx, "& .MuiFormHelperText-root": { fontSize: 10, ml: 0 } }}>
              <MenuItem value="">— Select a group —</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={String(g.id)}>
                  {g.name}{typeof g.devices_count === "number" ? ` · ${g.devices_count} screen${g.devices_count === 1 ? "" : "s"}` : ""}
                </MenuItem>
              ))}
            </TextField>

            <Typography sx={signageLabelSx}>Menu (playlist) *</Typography>
            <TextField select fullWidth size="small" value={playlistId === "" ? "" : String(playlistId)}
              onChange={(e) => setPlaylistId(e.target.value === "" ? "" : Number(e.target.value))}
              disabled={groupId === ""}
              helperText={groupId === "" ? "Select a group first." : "The playlist to play on this schedule."}
              sx={{ mb: 1, ...signageInputSx, "& .MuiFormHelperText-root": { fontSize: 10, ml: 0 } }}>
              <MenuItem value="">— Select a playlist —</MenuItem>
              {playlists.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
            </TextField>
          </SignageSection>

          <SignageSection icon={<EventIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
            title="When it runs" accentColor="#7c3aed">
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75 }}>DAYS</Typography>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 2 }}>
              {WEEK_DAYS.map((label, idx) => {
                const on = dayIsSet(days, idx);
                return (
                  <Chip key={label} label={label} size="small"
                    onClick={() => setDays(toggleDay(days || 0, idx))}
                    sx={{ fontWeight: 700, cursor: "pointer", bgcolor: on ? NAVY : "#EEF2F6", color: on ? "#fff" : TEXT_PRIMARY }} />
                );
              })}
              <Tooltip title="Every day"><Chip label="All" size="small" variant="outlined"
                onClick={() => setDays(0)} sx={{ fontWeight: 700, cursor: "pointer" }} /></Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Switch checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
              <Typography sx={{ fontSize: 14 }}>All day</Typography>
            </Box>
            {!allDay && (
              <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                <TimePicker label="Start" value={start} onChange={setStart}
                  slotProps={{ textField: { size: "small", fullWidth: true } }} />
                <TimePicker label="End" value={end} onChange={setEnd}
                  slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <DatePicker label="Start date (optional)" value={startDate} onChange={setStartDate}
                slotProps={{ textField: { size: "small", fullWidth: true } }} />
              <DatePicker label="End date (optional)" value={endDate} onChange={setEndDate}
                slotProps={{ textField: { size: "small", fullWidth: true } }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField label="Priority" type="number" size="small" value={priority}
                onChange={(e) => setPriority(Math.max(0, Number(e.target.value)))}
                helperText="Higher wins when two menus overlap." sx={{ maxWidth: 200 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <Typography sx={{ fontSize: 14 }}>Enabled now</Typography>
              </Box>
            </Box>
          </SignageSection>

          {summary && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2.5, border: `1px solid ${BORDER}`, bgcolor: "#ecfeff" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#0e7490", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
                Summary
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{summary}</Typography>
            </Paper>
          )}

          {conflicts && conflicts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This overlaps: {conflicts.map((c) => `${c.menu} (${c.days.join("/")} ${to12h(c.start_time)}–${to12h(c.end_time)})`).join("; ")}.
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <Button onClick={() => navigate("/menu-schedules/list")} sx={{ textTransform: "none" }}>Cancel</Button>
            {conflicts && conflicts.length > 0 && (
              <Button color="warning" disabled={saving} onClick={() => save(true)}
                sx={{ textTransform: "none", fontWeight: 700 }}>Schedule anyway</Button>
            )}
            <Button variant="contained" disabled={!canSave} onClick={() => save(false)}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: NAVY_DARK }} /> : undefined}
              sx={{
                textTransform: "none", fontWeight: 700, px: 3, color: NAVY_DARK,
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
              }}>
              {saving ? "Creating…" : "Create Menu Schedule"}
            </Button>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY }}>
            Tip: a menu schedule is a playlist scheduled inside a group. Priority and menu type
            decide which menu wins when several overlap — manage those on the schedule's Rule Priority tab.
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default MenuScheduleCreate;
